/**
 * Custom Fields utilities.
 *
 * Dynamic form fields for listings — defined by marketplace admin,
 * filled by providers, displayed to buyers.
 *
 * Field schema from CUSTOM_FIELDS_JSON env var:
 * [
 *   {
 *     "id": "vehicle_mileage",
 *     "label": "Mileage",
 *     "type": "number",          — text | number | select | checkbox | date | url
 *     "required": true,
 *     "unit": "km",
 *     "options": null,           — for select type: ["Option A", "Option B"]
 *     "category": "vehicles"     — optional: only show for this listing category
 *   }
 * ]
 *
 * Values stored in listing.publicData.customFields = { vehicle_mileage: 50000 }
 */

export const FIELD_TYPES = ['text', 'number', 'select', 'checkbox', 'date', 'url'];

/**
 * Validate a field value against its schema.
 * @param {Object} field — field schema
 * @param {*} value
 * @returns {string|null} — error or null
 */
export const validateFieldValue = (field, value) => {
  if (field.required && (value === null || value === undefined || value === '')) {
    return `${field.label} is required`;
  }
  if (value === null || value === undefined || value === '') return null;

  switch (field.type) {
    case 'number':
      if (isNaN(parseFloat(value))) return `${field.label} must be a number`;
      break;
    case 'select':
      if (Array.isArray(field.options) && !field.options.includes(value)) {
        return `${field.label} must be one of: ${field.options.join(', ')}`;
      }
      break;
    case 'url':
      try { new URL(value); } catch (_) { return `${field.label} must be a valid URL`; }
      break;
    case 'date':
      if (isNaN(new Date(value).getTime())) return `${field.label} must be a valid date`;
      break;
    default:
      break;
  }
  return null;
};

/**
 * Validate all custom field values.
 * @param {Object[]} fields — field schemas
 * @param {Object} values — { fieldId: value }
 * @returns {Object} — { fieldId: errorMessage }
 */
export const validateAllFields = (fields, values) => {
  const errors = {};
  for (const field of fields) {
    const err = validateFieldValue(field, values?.[field.id]);
    if (err) errors[field.id] = err;
  }
  return errors;
};

/**
 * Filter fields applicable to a listing category.
 * @param {Object[]} fields
 * @param {string|null} category
 * @returns {Object[]}
 */
export const getFieldsForCategory = (fields, category) => {
  if (!Array.isArray(fields)) return [];
  return fields.filter(f => !f.category || f.category === category);
};

/**
 * Format a custom field value for display.
 * @param {*} value
 * @param {Object} field
 * @returns {string}
 */
export const formatFieldValue = (value, field) => {
  if (value === null || value === undefined) return '—';
  if (field.type === 'checkbox') return value ? 'Yes' : 'No';
  if (field.unit) return `${value} ${field.unit}`;
  return String(value);
};
